import * as Sentry from '@sentry/vue'
import { AxiosError } from 'axios'
import _ from 'lodash'
import { ComponentPublicInstance } from 'vue'
import { Router } from 'vue-router'

import app from '@/app'
import settings from '@/settings'
import { DecoratedError } from '@/types'

const ERROR_LEVELS = {
  NETWORK: 'network',
  COMPONENT: 'component',
  BOOT: 'boot',
  SYSTEM: 'system',
}

const processError = (error: Error, moduleName: string, options = {}) => {
  const decoratedError = ErrService.decorateError(moduleName, error, options)
  ErrService.handleErrorNotifications(decoratedError)
  ErrService.sendError(decoratedError)
  console.error(decoratedError)
}

const createSyntheticError = (
  message: string | Event | null | undefined,
  source?: string,
  lineno?: number,
  colno?: number,
): Error => {
  const errorMessage =
    typeof message === 'string'
      ? message
      : message instanceof Event
        ? 'Event error'
        : 'Unknown error'
  const syntheticError = new Error(errorMessage)
  if (source || lineno || colno) {
    syntheticError.stack = `at ${source || 'unknown source'}:${lineno || '?'}:${colno || '?'}`
  }
  return syntheticError
}

window.onerror = function (message, source, lineno, colno, error) {
  if (!error) {
    const syntheticError = createSyntheticError(message, source, lineno, colno)
    processError(syntheticError, 'WINDOW', {
      errorLevel: ERROR_LEVELS.SYSTEM,
      customMessage: 'An unexpected error occurred',
    })
    return
  }

  if (error instanceof Error) {
    processError(error, 'WINDOW')
  } else {
    const syntheticError = createSyntheticError(
      typeof error === 'string' ? error : 'Non-error object thrown',
    )
    if (typeof error === 'object') {
      try {
        syntheticError.stack = JSON.stringify(error)
      } catch {
        syntheticError.stack = 'Unable to stringify error object'
      }
    }
    processError(syntheticError, 'WINDOW')
  }
}

window.addEventListener('unhandledrejection', function (event) {
  event.preventDefault()

  if (!event.reason) {
    const syntheticError = createSyntheticError(
      'Unhandled Promise rejection with no reason provided',
    )
    processError(syntheticError, ERROR_LEVELS.NETWORK, {
      errorLevel: ERROR_LEVELS.NETWORK,
    })
    return
  }

  const error = event.reason
  if (error instanceof Error) {
    const transformedError = error as DecoratedError | AxiosError | Error
    const options: DecoratedError['options'] =
      'options' in transformedError
        ? {
            ...transformedError.options,
            errorLevel: transformedError.options.errorLevel || ERROR_LEVELS.NETWORK,
          }
        : ({} as DecoratedError['options'])
    const errorLevel = 'errorLevel' in options ? options.errorLevel : ERROR_LEVELS.NETWORK
    processError(transformedError, errorLevel, options)
  } else {
    const syntheticError = createSyntheticError(
      typeof error === 'string' ? error : 'Promise rejected with non-error value',
    )

    if (typeof error === 'object') {
      try {
        syntheticError.stack = `Non-error promise rejection: ${JSON.stringify(error)}`
      } catch {
        syntheticError.stack = 'Unable to stringify rejected value'
      }
    } else {
      syntheticError.stack = `Non-error promise rejection: ${String(error)}`
    }

    processError(syntheticError, ERROR_LEVELS.NETWORK, {
      errorLevel: ERROR_LEVELS.NETWORK,
    })
  }
})

const ErrService = {
  isSentryEnabled: !!(
    settings.services.sentry.enabled &&
    settings.services.sentry.dsn &&
    settings.services.sentry.env
  ),

  initSentry(appInstance: typeof app, router: Router) {
    if (!this.isSentryEnabled) return

    Sentry.init({
      app: appInstance,
      dsn: settings.services.sentry.dsn,
      integrations: [
        Sentry.browserTracingIntegration({
          router,
        }),
        Sentry.globalHandlersIntegration({
          onerror: false,
          onunhandledrejection: false,
        }),
        Sentry.replayIntegration(),
      ],
      environment: settings.services.sentry.env,
      replaysSessionSampleRate: 0.01,
      replaysOnErrorSampleRate: 0.1,
      tracesSampleRate: 0.01,
      normalizeDepth: 10,
      sendDefaultPii: true,
      ignoreErrors: [
        'Failed to fetch dynamically imported module',
        'Unable to preload CSS',
        "Cannot read property 'parentNode' of null",
        "Cannot read properties of undefined (reading 'fireChangeEvent')",
        "Cannot read properties of undefined (reading 'fireReadyEvent')",
        "Cannot read properties of undefined (reading 'setCurrentPosition')",
        "Cannot read properties of undefined (reading 'setDefaultPosition')",
        "Cannot read properties of undefined (reading 'setMaxSize')",
        "Cannot read properties of undefined (reading 'setScreenSize')",
        "Cannot read properties of undefined (reading 'fireEvent')",
        "Cannot read properties of undefined (reading 'audioVolumeChange')",
        "The email address or password you entered doesn't match our records. Please try again",
      ],
      beforeSend(event, hint) {
        if (hint.originalException === 'Timeout') return null
        return event
      },
    })
  },

  sendError(error: Error | DecoratedError) {
    if (!error || !this.isSentryEnabled) return

    const isDecoratedError = (err: Error | DecoratedError): err is DecoratedError =>
      err !== null && typeof err === 'object' && 'options' in err && err.options !== undefined

    if (isDecoratedError(error) && error.options?.sendError === false) return

    return Sentry.captureException(error)
  },

  sendMessage(msg: string) {
    if (!this.isSentryEnabled) return
    return Sentry.captureMessage(msg)
  },

  configureClient(value: string | null | undefined) {
    if (!this.isSentryEnabled) return

    const newValue = value ? { uid: value } : null
    Sentry.getCurrentScope().setUser(newValue)
  },

  throwIgnore(error: DecoratedError | null | undefined) {
    if (!error) {
      throw new Error('Undefined error in throwIgnore')
    }
    error._ignore = true
    throw error
  },

  handleErrorNotifications: _.debounce(function (error: DecoratedError | null | undefined) {
    if (!error || !error.options || error.options.showNotification === false) return

    const toasted = app?.config?.globalProperties?.$toasted
    if (!toasted) {
      console.error('Toasted plugin is not registered')
      return
    }
    if (error.options?.customMessage) {
      toasted.show(error.options.customMessage, error.options.customActions)
    } else {
      switch (error.options.errorLevel) {
        case ERROR_LEVELS.NETWORK:
          toasted.show(
            '<span>Oops, network request error. Please try again or <a href="/about/contact-us">contact us</a></span>',
            {
              duration: 6000,
            },
          )
          break
        case ERROR_LEVELS.BOOT:
          setTimeout(() => {
            toasted.show('The site has encountered an error. Please try again.', {
              action: {
                text: 'Reload',
                onClick: () => {
                  window.location.reload()
                },
              },
            })
          }, 500)
          break
        case ERROR_LEVELS.COMPONENT:
        default:
          break
      }
    }
  }, 500),

  decorateError(moduleName: string | null | undefined, error: Error, options = {}): DecoratedError {
    if (!error) {
      throw new Error('Cannot decorate a null or undefined error')
    }
    const decoratedError = error as DecoratedError
    decoratedError.options = {
      errorLevel: '',
      customMessage: '',
      showNotification: true,
      sendError: true,
      ...options,
    }
    const safeModuleName = moduleName || ''
    this._formatErrorName(decoratedError, safeModuleName)
    this._extractMessageFromResponse(decoratedError)
    return decoratedError
  },

  _formatErrorName(decoratedError: DecoratedError, moduleName: string): void {
    switch (decoratedError.options.errorLevel) {
      case ERROR_LEVELS.NETWORK:
        decoratedError.name = '[NETWORK]'
        if (decoratedError.config) {
          const method = decoratedError.config.method?.toUpperCase() || 'UNKNOWN'
          let url = decoratedError.config.url || ''
          if (url && settings.services.api.baseUrl) {
            url = url.replace(settings.services.api.baseUrl, '')
          }
          decoratedError.name += ` - ${method} on ${url}`
        }
        break
      case ERROR_LEVELS.BOOT:
        decoratedError.name = `[BOOT] - [${moduleName.toUpperCase() || 'APP'}]`
        break
      case ERROR_LEVELS.COMPONENT:
        decoratedError.name = `[COMPONENT] - [${moduleName.toUpperCase() || '...'}]`
        break
      case ERROR_LEVELS.SYSTEM:
        decoratedError.name = `[SYSTEM] - [${moduleName.toUpperCase() || 'UNKNOWN'}]`
        break
    }
  },

  _extractMessageFromResponse(decoratedError: DecoratedError): void {
    if (decoratedError.response?.data) {
      const responseData = decoratedError.response.data
      let message = decoratedError.message

      if (Array.isArray(responseData)) {
        const messages = responseData.map((el) => el?.message || '').filter(Boolean)

        if (messages.length > 0) {
          message = messages.join('. ')
        }
      } else if (typeof responseData === 'object' && responseData.message) {
        message = String(responseData.message)
      } else if (responseData) {
        message = String(responseData)
      }

      decoratedError.message = message
    }
  },

  _getComponentName(vm: ComponentPublicInstance | null): string {
    if (!vm) return ''

    try {
      if (vm && vm.$) {
        if (vm.$.type && typeof vm.$.type === 'object') {
          const name = vm.$.type.name || vm.$.type.__name
          if (name) return name
        }

        if (vm.$.vnode && vm.$.vnode.type) {
          const vnodeType = vm.$.vnode.type
          if (typeof vnodeType === 'object' && 'name' in vnodeType) {
            return vnodeType.name || ''
          }
        }
      }
    } catch (nameError) {
      console.error('Error getting component name:', nameError)
    }

    return ''
  },

  init(appInstance: typeof app, router: Router) {
    if (!appInstance || !router) {
      console.error('Cannot initialize ErrService without app instance and router')
      return
    }
    appInstance.config.errorHandler = (rawError, vm) => {
      if (!rawError) return false
      const error = rawError instanceof Error ? rawError : new Error(String(rawError))
      const componentName = vm ? this._getComponentName(vm as ComponentPublicInstance) : ''
      processError(error, componentName || 'COMPONENT', {
        errorLevel: ERROR_LEVELS.COMPONENT,
      })
    }

    this.initSentry(appInstance, router)
  },
}

export default ErrService
